import React, {useMemo} from 'react';
import {Table, Button, Dropdown, Input, Grid, Icon} from 'semantic-ui-react';
import {useTable, usePagination, useFilters} from 'react-table';
import {pageSizes} from '../../../common/constants/lists';
import {Filter, DefaultColumnFilter} from './agenciesTableFilters';
import AgenciesTableInputView from './agenciesTableInputView';
import {getDropdownOptions} from '../../../common/util/dropdown';

const AgenciesTable = ({tableColumns, tableData, agencySelectedToEdit, getAgencyEditingValue,
  setAgencyRef, handleAgencyValue, handleAgencyEdit, handleAgencyUpdate, handleAgencyEditCancel,
  provincesList, dealersList, addAgency, changeNewAgencyValue, saveNewAgency, cancelAddAgency,
  validateAgency, handleAgencyStatus}) => {
  const data = useMemo(() => tableData, [tableData]);
  const columns = useMemo(() => tableColumns, []);

  const TablePaginator = ({
    page,
    canPreviousPage,
    pageIndex,
    pageOptions,
    pageSize,
    canNextPage,
    nextPage,
    gotoPage,
    previousPage,
    setPageSize,
  }) => {
    if (page.length) {
      return (
        <Grid className="pagination">
          <Grid.Row>
            <Grid.Column width={4} textAlign="left">
              <Button
                className="navigation-button"
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
                content="Anterior"
              />
            </Grid.Column>
            <Grid.Column width={4} textAlign="right">
              <span>páginas </span>
              <span>
                <Input
                  className="pages-input"
                  type="number"
                  value={pageIndex + 1}
                  min="1"
                  max={pageOptions.length}
                  onChange={(e, {value}) => {
                    const page = value ? Number(value) - 1 : 0;
                    if (page < 1 || page > pageOptions.length) {
                      e.target.value = 1;
                    }
                    gotoPage(page);
                  }}
                />
              </span>{' '}
              <span>de {pageOptions.length}</span>
            </Grid.Column>
            <Grid.Column width={4} textAlign="left">
              <Dropdown
                className="page-sizes-selection"
                selection
                upward
                compact
                value={pageSize}
                options={pageSizes}
                onChange={(e, {value}) => {
                  setPageSize(Number(value));
                }}
              />
              <span> filas</span>
            </Grid.Column>
            <Grid.Column width={4} textAlign="right">
              <Button
                className="navigation-button"
                onClick={() => nextPage()}
                disabled={!canNextPage}
                content="Siguiente"
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      );
    }
    return null;
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    'state': {pageIndex, pageSize},
  } = useTable(
    {
      columns,
      data,
      'defaultColumn': {'Filter': DefaultColumnFilter},
      'initialState': {'pageIndex': 0, 'pageSize': 10},
      'autoResetFilters': false,
    },
    useFilters,
    usePagination,

    (hooks) => {
      hooks.visibleColumns.push((columns, i) => [
        {
          'id': 'editAgencyButton',
          'Cell': ({row}) => {
            return (
              <div key={`body-cell${i}`} className='flex'>
                {!agencySelectedToEdit(row.original) ?
                  <Icon name='pencil' link size='big'
                    onClick={(e, input) => handleAgencyEdit(e, row.original)} /> :
                  <>
                    <Icon name='save' color='green' link={!validateAgency()}
                      disabled={validateAgency()} size='big'
                      onClick={(e, input) => handleAgencyUpdate()} />
                    <Icon name='cancel' color='red' link size='big'
                      onClick={(e, input) => handleAgencyEditCancel()} />
                  </>
                }
              </div>
            );
          },
        },
        ...columns.map((column, i) => {
          return {...column,
            'Cell': (item) => {
              return (
                <div key={`body-cell${i}`} style={{'textAlign': 'center'}}>
                  {!agencySelectedToEdit(item.row.original) ?
                    <AgenciesTableInputView item={item} editable={false}
                      provincesList={provincesList} dealersList={dealersList}
                      handleAgencyStatus={handleAgencyStatus.bind(this)} /> :
                    <AgenciesTableInputView item={item} editable={true}
                      provincesList={provincesList} dealersList={dealersList}
                      validateAgency={validateAgency}
                      edit={{
                        'getAgencyEditingValue': getAgencyEditingValue.bind(this),
                        'handleAgencyValue': handleAgencyValue.bind(this),
                        'setAgencyRef': setAgencyRef.bind(this),
                      }}
                    />}
                </div>
              );
            },
          };
        }),
      ]);
    }
  );

  return (
    <>
      <Table className='interactive' striped compact {...getTableProps()}>
        <Table.Header>
          {headerGroups.map((headerGroup, i) => (
            <Table.Row key={`headers-${i}`} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, index) => (
                <Table.HeaderCell
                  key={`title-${index}`}
                  textAlign='center'
                  width={column.width}
                  {...column.getHeaderProps()}
                >
                  {column.render('Header')}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          ))}
          {headerGroups.map((headerGroup, i) => (
            <>
              <Table.Row key={`filters-${i}`} id='filters'>
                {headerGroup.headers.map((column, i) => (
                  <Table.HeaderCell
                    key={`filter-${i}`}
                    textAlign='center'
                    width={column.width}
                  >
                    {<Filter column={column} />}
                  </Table.HeaderCell>
                ))}
              </Table.Row>
              {addAgency ?
                <Table.Row key={`new-register${i}`} id='new-register'>
                  {headerGroup.headers.map((column, index) => (
                    <Table.HeaderCell
                      style={{'backgroundColor': 'white'}}
                      key={`filter-col-${index}`}
                      textAlign='center'
                    >
                      {!(column.id === 'editAgencyButton') ?
                        column.id === 'driverArray' || column.id === 'status' ? '' :
                          column.id === 'identification' ?
                            <Dropdown fluid selection value={addAgency[column.id] || ''}
                              options={getDropdownOptions(dealersList, 'identification', 'name')}
                              onChange={(e, input) => {
                                changeNewAgencyValue(e, column, input.value);
                              }}
                            /> :
                            column.id === 'ruc' ? <div>{(addAgency && addAgency.identification) || ''}</div> :
                              column.id === 'provinceCode' ?
                                <Dropdown fluid search selection id='provinceCode'
                                  value={addAgency[column.id]}
                                  options={getDropdownOptions(provincesList, 'code', 'name')}
                                  onChange={(e, input) => changeNewAgencyValue(e, column, input.value)} /> :
                                column.id === 'cantonCode' ?
                                  <Dropdown fluid search selection id='cantonCode'
                                    value={addAgency[column.id]}
                                    options={addAgency.provinceCode ? getDropdownOptions(
                                      provincesList.find((x) =>
                                        x.code === addAgency.provinceCode).array_canton,
                                      'cantonCode', 'cantonName') : []}
                                    onChange={(e, input) => changeNewAgencyValue(e, column, input.value)} /> :
                                  <Input fluid disabled={column.id === 'courierName'}
                                    value={addAgency[column.id] || ''}
                                    onChange={(e, input) => {
                                      changeNewAgencyValue(e, column, input.value);
                                    }}
                                  /> :
                        <>
                          <Icon name='save' color='green' link={!validateAgency('add')}
                            size='big' disabled={validateAgency('add')}
                            onClick={(e) => saveNewAgency(e)} />
                          <Icon name='cancel' color='red' link size='big'
                            onClick={(e) => cancelAddAgency(e)} />
                        </>
                      }
                    </Table.HeaderCell>
                  ))}
                </Table.Row> : ''
              }
            </>
          ))}
        </Table.Header>
        <Table.Body {...getTableBodyProps()}>
          {!page.length && (
            <Table.Row>
              <Table.Cell colSpan={11} textAlign='center'>
                No hay datos disponibles
              </Table.Cell>
            </Table.Row>
          )}
          {page.length > 0 &&
            page.map((row, i) => {
              prepareRow(row);
              return (
                <Table.Row key={`table-row${i}`} {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    return cell.column.id === 'editAgencyButton' ?
                      <Table.Cell textAlign='center' {...cell.getCellProps()}>
                        {cell.render('Cell')}
                      </Table.Cell> :
                      agencySelectedToEdit(row.original) ?
                        <Table.Cell textAlign='center' {...cell.getCellProps()}>
                          {cell.render('Cell')}
                        </Table.Cell> :
                        <Table.Cell textAlign='center' {...cell.getCellProps()} >
                          {cell.render('Cell')}
                        </Table.Cell>;
                  })}
                </Table.Row>
              );
            })}
        </Table.Body>
      </Table>
      <TablePaginator
        page={page}
        canPreviousPage={canPreviousPage}
        pageIndex={pageIndex}
        pageOptions={pageOptions}
        pageSize={pageSize}
        canNextPage={canNextPage}
        nextPage={nextPage}
        gotoPage={gotoPage}
        previousPage={previousPage}
        setPageSize={setPageSize}
      />
    </>
  );
};

export default AgenciesTable;
